/*
 * ILS — Custom product page (v12 + TASK-1860 batch 1) — UI glue.
 * Behaviours:
 *   - Gallery: one-swipe-per-image carousel with infinite wrap-around
 *     (translate-based, no native scroll snap).
 *   - Dots + thumbs sync.
 *   - Tab switching.
 *   - Quantity +/- in the sticky panel.
 *   - Sticky panel: always visible, live unit-price × qty sum.
 *   - Catalog-style star rendering for any `.ils-pcl__stars[data-rate]`
 *     present on the PDP (re-uses the same SVG path as
 *     custom-collection.js so the visual stays identical).
 *   - Sticky bottom payment row: enable a slow marquee when the chip
 *     track doesn't fit in the available width.
 *
 * Variant change / AJAX add-to-cart / price update are handled by Dawn's
 * <product-info>, <variant-selects> and <product-form>. After every
 * variant-change AJAX swap the subtree is re-rendered, so every init
 * function here is idempotent and runs again on `product-info:loaded`.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- European money formatter (€1.234,56) ---------- */
  function formatMoneyCents(cents) {
    /* TASK-5795 — the PDP sticky sum and the "Save X" pill are PRESENTMENT
       money (measured live: the sticky unit read "€57,00" on /en-se/ for a
       57,00 kr variant). Printed in the market's own format; null falls through
       to the legacy euro body, so EUR markets keep today's bytes. */
    if (window.ILSMoney && window.ILSMoney.on()) {
      var _p5795 = window.ILSMoney.fromCents(cents);
      if (_p5795 !== null) return _p5795;
    }
    var sign = cents < 0 ? '-' : '';
    var abs = Math.abs(cents | 0);
    var euros = Math.floor(abs / 100);
    var c = String(abs % 100);
    while (c.length < 2) c = '0' + c;
    var eFormatted = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    return sign + '€' + eFormatted + (c === '00' ? '' : ',' + c);
  }

  // TASK-2569: localized "Save €X" pill on the main PDP price. The translated
  // `custom.product.save_amount` template (e.g. DE "Sparen Sie __AMOUNT__") is
  // exposed on the price host as data-save-tpl, with the money slot as a
  // brace-free sentinel so Liquid can carry it safely in an HTML attribute. The
  // amount is formatted as PLAIN TEXT (formatMoneyCents) and assigned via
  // textContent, so it can never leak markup (the TASK-2565 bug class). Falls
  // back to English "Save €X" if the host/attr is absent (other langs until
  // their translations run).
  function saveLabel(host, diffCents) {
    var amount = formatMoneyCents(diffCents);
    var tpl = host && host.getAttribute && host.getAttribute('data-save-tpl');
    if (tpl && /__AMOUNT__|\{\{\s*amount\s*\}\}/.test(tpl)) {
      return tpl.replace(/__AMOUNT__|\{\{\s*amount\s*\}\}/g, amount);
    }
    return 'Save ' + amount;
  }

  /* ---------- Star renderer ----------
     Same SVG path + colours as assets/custom-collection.js so the PDP
     and catalog cards share one visual. Renders into any element with
     class `ils-pcl__stars` and a `data-rate` attribute. */
  var STAR_PATH = 'M12 2.6c.55 0 1.04.33 1.27.83l2.3 4.96 5.43.7c.55.07.99.45 1.16.97.16.51-.01 1.07-.42 1.43l-4.02 3.52 1.06 5.4c.1.54-.12 1.08-.57 1.4-.45.31-1.04.34-1.52.07L12 18.86l-4.69 2.61c-.48.27-1.07.24-1.52-.07-.45-.32-.67-.86-.57-1.4l1.06-5.4-4.02-3.52c-.41-.36-.58-.92-.42-1.43.17-.52.61-.9 1.16-.97l5.43-.7 2.3-4.96c.23-.5.72-.83 1.27-.83z';

  function starSvg(state) {
    if (state === 'full') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    if (state === 'dim') return '<svg viewBox="0 0 24 24" class="dim" aria-hidden="true"><path d="' + STAR_PATH + '"/></svg>';
    var gid = 'ilshgp' + Math.random().toString(36).slice(2, 8);
    return '<svg viewBox="0 0 24 24" aria-hidden="true">' +
      '<defs><linearGradient id="' + gid + '"><stop offset="50%" stop-color="var(--ils-star)"/><stop offset="50%" stop-color="var(--ils-star-dim)"/></linearGradient></defs>' +
      '<path d="' + STAR_PATH + '" fill="url(#' + gid + ')"/></svg>';
  }

  function renderStars(root) {
    var scope = root || document;
    scope.querySelectorAll('.ils-pcl__stars').forEach(function (el) {
      if (el.dataset.ilsStarsBuilt === '1') return;
      var rate = parseFloat(el.getAttribute('data-rate')) || 0;
      var html = '';
      for (var i = 1; i <= 5; i++) {
        if (rate >= i) html += starSvg('full');
        else if (rate >= i - 0.5) html += starSvg('half');
        else html += starSvg('dim');
      }
      el.innerHTML = html;
      el.dataset.ilsStarsBuilt = '1';
    });
  }

  /* ---------- Gallery (one-at-a-time + infinite) ---------- */
  function initGallery(root) {
    var viewport = root.querySelector('[id^="ilsGalleryViewport-"]');
    var gallery = root.querySelector('[id^="ilsGallery-"]');
    if (!viewport || !gallery) return;

    var slides = gallery.querySelectorAll('.ils-pp__slide');
    var count = slides.length;
    if (!count) return;

    var dots = root.querySelectorAll('[id^="ilsDots-"] .dot');
    var thumbs = root.querySelectorAll('[id^="ilsThumbs-"] .thumb');

    var currentIdx = 0;

    function applyTransform(animated) {
      if (animated) gallery.classList.remove('is-dragging');
      else gallery.classList.add('is-dragging');
      gallery.style.transform = 'translate3d(' + (-currentIdx * 100) + '%, 0, 0)';
    }
    function setActive(i) {
      for (var k = 0; k < dots.length; k++) dots[k].classList.toggle('active', k === i);
      for (var k2 = 0; k2 < thumbs.length; k2++) thumbs[k2].classList.toggle('active', k2 === i);
    }
    function goTo(idx, animated) {
      currentIdx = ((idx % count) + count) % count;
      applyTransform(animated !== false);
      setActive(currentIdx);
    }
    function next() { goTo(currentIdx + 1, true); }
    function prev() { goTo(currentIdx - 1, true); }

    goTo(0, false);
    requestAnimationFrame(function () { applyTransform(true); });

    for (var i = 0; i < thumbs.length; i++) {
      (function (idx) {
        thumbs[idx].addEventListener('click', function () { goTo(idx, true); });
      })(i);
    }

    /* Pointer-based swipe: any drag past the threshold advances one
       slide (and wraps around at the ends). Below the threshold the
       view snaps back to the current slide. Pointer events cover
       touch + mouse + pen with one path. */
    var SWIPE_THRESHOLD = 40;
    var startX = 0, startY = 0, startT = 0;
    var dragging = false, lockedAxis = null;
    var pointerId = null;

    function onDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      lockedAxis = null;
      startX = e.clientX;
      startY = e.clientY;
      startT = Date.now();
      pointerId = e.pointerId;
      gallery.classList.add('is-dragging');
      try { viewport.setPointerCapture(pointerId); } catch (_) {}
    }
    function onMove(e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!lockedAxis) {
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
          lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
      }
      if (lockedAxis !== 'x') return;
      e.preventDefault();
      var viewportW = viewport.clientWidth || 1;
      var pct = (dx / viewportW) * 100;
      gallery.style.transform = 'translate3d(calc(' + (-currentIdx * 100) + '% + ' + pct.toFixed(3) + '%), 0, 0)';
    }
    function onUp(e) {
      if (!dragging || (e.pointerId !== undefined && e.pointerId !== pointerId)) return;
      dragging = false;
      try { viewport.releasePointerCapture(pointerId); } catch (_) {}
      pointerId = null;
      if (lockedAxis !== 'x') {
        applyTransform(true);
        return;
      }
      var dx = e.clientX - startX;
      var dt = Date.now() - startT;
      var fast = dt < 250 && Math.abs(dx) > 18;
      if (dx <= -SWIPE_THRESHOLD || (fast && dx < 0)) next();
      else if (dx >= SWIPE_THRESHOLD || (fast && dx > 0)) prev();
      else goTo(currentIdx, true);
    }

    viewport.addEventListener('pointerdown', onDown);
    viewport.addEventListener('pointermove', onMove);
    viewport.addEventListener('pointerup', onUp);
    viewport.addEventListener('pointercancel', onUp);
    viewport.addEventListener('pointerleave', function (e) {
      if (dragging && e.pointerId === pointerId) onUp(e);
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root) {
    var tabs = root.querySelectorAll('[id^="ilsTabs-"] .ils-pp__tab');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) { t.classList.remove('active'); });
        tab.classList.add('active');
        var key = tab.dataset.p;
        root.querySelectorAll('.ils-pp__panel').forEach(function (p) { p.classList.remove('active'); });
        var panel = root.querySelector('[id^="p-' + key + '-"]');
        if (panel) panel.classList.add('active');
      });
    });
  }

  /* ---------- Quantity + sticky sum (point 14) ----------
     The qty stepper lives inside the sticky panel. On every qty change
     the trailing «· €X» on the button is recomputed from the unit
     cents currently stamped on the sticky's `data-unit-cents`. The
     attribute is restamped by `syncStickyToVariant` after every
     `variant-change` (Dawn's PUB_SUB event published from
     product-info.js after its AJAX swap). The sticky panel itself sits
     outside `#price-{section_id}`, so Dawn does not repaint it — we do.
     `recompute` re-reads `dataset.unitCents` on each call so it always
     reflects the latest variant. */
  function initStickyAtc(root) {
    var sticky = root.querySelector('[data-ils-sticky-atc]');
    if (!sticky) return;
    var qtyWrap = sticky.querySelector('.ils-pp__qty');
    var qtyInput = sticky.querySelector('[data-ils-qty]');
    var sumEl = sticky.querySelector('[data-ils-sticky-sum]');
    var unitEl = sticky.querySelector('[data-ils-sticky-unit]');

    // TASK-1865 — hide the «−» button when qty equals the minimum (1).
    // Toggle a class on the stepper container instead of removing the
    // button so the CSS can restore it instantly when qty grows.
    function syncMinusVisibility() {
      if (!qtyWrap || !qtyInput) return;
      var min = parseInt(qtyInput.min, 10) || 1;
      var v = parseInt(qtyInput.value, 10);
      if (!isFinite(v)) v = min;
      qtyWrap.classList.toggle('is-min', v <= min);
    }

    function recompute() {
      var unitCents = parseInt(sticky.dataset.unitCents || '0', 10) || 0;
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      if (sumEl) sumEl.textContent = formatMoneyCents(unitCents * qty);
      if (unitEl && unitCents > 0) unitEl.textContent = formatMoneyCents(unitCents);
      syncMinusVisibility();
    }
    recompute();

    if (qtyInput) {
      qtyInput.addEventListener('input', recompute);
      qtyInput.addEventListener('change', recompute);
    }
    sticky.querySelectorAll('.ils-pp__qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var v = parseInt(qtyInput.value, 10) || 1;
        var min = parseInt(qtyInput.min, 10) || 1;
        if (btn.dataset.q === '+') qtyInput.value = v + 1;
        else if (v > min) qtyInput.value = v - 1;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // Expose recompute so the variant-change subscriber can refresh
    // unit + sum text after restamping data-unit-cents.
    sticky.__ilsRecompute = recompute;
  }

  /* ---------- Variant-change → sticky price sync ----------
     Dawn's product-info.js publishes PUB_SUB_EVENTS.variantChange after
     every variant-change AJAX swap with the full new variant payload
     (parsed from the fetched `[data-selected-variant]` JSON). The
     sticky panel is inside <product-info> but outside `#price-{id}`,
     which is the only fragment Dawn restamps in-place. Restamp
     `data-unit-cents` from variant.price (integer cents) and re-run the
     sticky recompute so unit + sum + (× qty) match the new variant
     instantly. Hidden `input[name="id"]` is already updated by Dawn's
     `updateVariantInputs`, so add-to-cart from the sticky form posts
     the newly selected variant id. */
  function applyVariantToSticky(productInfo, variant) {
    if (!productInfo || !variant) return;
    var sticky = productInfo.querySelector('[data-ils-sticky-atc]');
    if (!sticky) return;
    var cents = parseInt(variant.price, 10);
    if (!isFinite(cents)) return;
    sticky.dataset.unitCents = String(cents);
    if (typeof sticky.__ilsRecompute === 'function') {
      sticky.__ilsRecompute();
    }
  }

  /* ---------- Variant-change → upper price block + -X% badge ----------
     Dawn morphs only `#price-{section_id}` (old/now/Save) via
     `updateSourceFromDestination('price')`. Two things stay outside that
     fragment and therefore aren't repainted:
       1) On preview, until the AJAX URL also carries `preview_theme_id`
          (see patchProductInfoPreviewUrls), Dawn's response is a 404
          from the published theme and the morph silently no-ops — even
          inside the fragment. So we paint the price ourselves from the
          published variant payload (also covers any future case where
          the morph lags).
       2) The status-badges row `.ils-pp__badges--status` (which holds
          the red `−X%` chip) lives below the price block, OUTSIDE the
          morphed fragment. JS owns its sale state.
     Idempotent + cheap: re-runs on every variantChange; matches Dawn's
     final morphed DOM so there is no flicker / no double state.
     TASK-1885. */
  function applyVariantToPrice(productInfo, variant) {
    if (!productInfo || !variant) return;
    var price = productInfo.querySelector('.ils-pcl__price.ils-pp__price');
    if (!price) return;

    var nowCents = parseInt(variant.price, 10);
    if (!isFinite(nowCents)) return;
    var compCents = parseInt(variant.compare_at_price, 10);
    var onSale = isFinite(compCents) && compCents > nowCents && variant.available !== false;

    // .ils-pcl__now — always present in Liquid, just restamp the text.
    var nowEl = price.querySelector('.ils-pcl__now');
    if (nowEl) nowEl.textContent = formatMoneyCents(nowCents);

    // .ils-pcl__old — present only on sale. Create when missing, remove off-sale.
    var oldEl = price.querySelector('.ils-pcl__old');
    if (onSale) {
      if (!oldEl) {
        oldEl = document.createElement('span');
        oldEl.className = 'ils-pcl__old';
        price.insertBefore(oldEl, nowEl || price.firstChild);
      }
      oldEl.textContent = formatMoneyCents(compCents);
    } else if (oldEl) {
      oldEl.parentNode.removeChild(oldEl);
    }

    // .ils-pcl__save — green "Save €X" pill, on-sale only.
    var saveEl = price.querySelector('.ils-pcl__save');
    if (onSale) {
      if (!saveEl) {
        saveEl = document.createElement('span');
        saveEl.className = 'ils-pcl__save';
        price.appendChild(saveEl);
      }
      saveEl.textContent = saveLabel(price, compCents - nowCents);
    } else if (saveEl) {
      saveEl.parentNode.removeChild(saveEl);
    }

    price.classList.toggle('is-sale', onSale);

    // .bg-status.bs-save — −X% chip in the status-badges row. The Liquid
    // ships a persistent span (hidden when off-sale) so JS only toggles
    // text + the [hidden] attribute, keeping other status chips intact.
    var saveBadge = productInfo.querySelector('.ils-pp__badges--status .bg-status.bs-save');
    if (onSale) {
      var pct = Math.round(((compCents - nowCents) / compCents) * 100);
      if (saveBadge) {
        saveBadge.textContent = '-' + pct + '%';
        saveBadge.hidden = false;
      }
    } else if (saveBadge) {
      saveBadge.hidden = true;
      saveBadge.textContent = '';
    }
  }

  function setupVariantPriceSync() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (window.__ilsStickyVariantSub) return;
    window.__ilsStickyVariantSub = subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
      var data = payload && payload.data;
      if (!data || !data.variant) return;
      var productInfo =
        (data.sectionId && document.querySelector('product-info[data-section="' + data.sectionId + '"].ils-pp-wrap')) ||
        document.querySelector('product-info.ils-pp-wrap');
      applyVariantToSticky(productInfo, data.variant);
      applyVariantToPrice(productInfo, data.variant);
    });
  }

  /* ---------- Sticky payment marquee (TASK-1869 bug 2) ----------
     The chip set is rendered twice in the Liquid template. The CSS
     keyframes animate from translateX(0) to translateX(var(--ils-pay-shift)),
     where `--ils-pay-shift` is the negative pixel distance from the first
     chip of set 1 to the first chip of set 2 — equal to `setW + gapX`.
     Measuring both in pixels (instead of relying on `-50%` of the track
     width) is what keeps the loop seamless: set 2's first chip lands
     exactly where set 1's first chip started, no overlap, no jump.
     Animation runs only when one set is wider than the visible wrap. */
  function initPayMarquee(root) {
    var wrap = root.querySelector('[data-ils-sticky-pay-marquee]');
    var track = root.querySelector('[data-ils-sticky-pay-track]');
    if (!wrap || !track) return;

    function evaluate() {
      wrap.classList.remove('is-mq');
      wrap.style.removeProperty('--ils-pay-mq-duration');
      track.style.removeProperty('--ils-pay-shift');

      var sets = track.querySelectorAll('.sa-pay-set');
      var firstSet = sets[0];
      var secondSet = sets[1];
      if (!firstSet) return;

      var setW = firstSet.getBoundingClientRect().width;
      var wrapW = wrap.clientWidth;
      if (!(setW > wrapW + 4)) return; // fits — leave static, no marquee

      // Gap at the set-to-set seam = margin-left of the second set's first
      // chip (CSS sets it to 6px; per-chip margin keeps the seam identical
      // to the internal chip spacing). Read live in case CSS changes.
      var gapX = 6;
      if (secondSet) {
        var seamChip = secondSet.querySelector('.sa-pay-chip');
        if (seamChip) {
          var m = parseFloat(getComputedStyle(seamChip).marginLeft);
          if (isFinite(m) && m >= 0) gapX = m;
        }
      }

      var shift = setW + gapX;
      track.style.setProperty('--ils-pay-shift', '-' + shift.toFixed(2) + 'px');
      // Fixed cycle duration: pre-TASK-1869 baseline was 18s per set, sped up 20% → 14.4s.
      var duration = 14.4;
      wrap.style.setProperty('--ils-pay-mq-duration', duration + 's');
      wrap.classList.add('is-mq');
    }
    evaluate();
    var rafToken = null;
    window.addEventListener('resize', function () {
      if (rafToken) cancelAnimationFrame(rafToken);
      rafToken = requestAnimationFrame(evaluate);
    });
  }

  /* ---------- PDP trust/proof marquee speed (TASK-2884) ----------
     The sticky row-2 "proof" strip (delivery · orders delivered · trusted
     customers · rating · tracked shipping) is a marquee: two IDENTICAL
     chip sets are rendered in the Liquid template and the CSS keyframe
     `ilsPpfScroll` animates the track from translateX(0) to
     translateX(-50%). Because -50% is relative to the track's OWN width
     and the two sets are identical (TASK-2884 also fixed the first set,
     which was missing the tracked-shipping chip), one cycle advances the
     belt by exactly one set — a seamless loop with no jump or gap. The
     endpoint is a concrete percentage, so it interpolates in every browser
     (unlike the sibling `ilsPayMarquee`, whose keyframe reads an
     unregistered `var()` — the trap documented for the cart pay-marquee).

     The CSS shipped a FIXED 21s-per-cycle duration, so the effective
     px/sec speed drifted per locale: German chips are wider than English,
     so the same 21s covered more pixels → a visibly faster belt. This
     normalises every locale to a constant 25 px/sec — the same principle
     already applied to the breadcrumb (custom-breadcrumb.js) and the cart
     payment marquee (initCdPayMarquee) — by measuring the live per-cycle
     distance (half the two-set track width) and setting
     `animation-duration = distance / 25`. Only the duration is touched;
     name/timing-function/iteration stay from the stylesheet, and the
     prefers-reduced-motion `animation:none` override still wins (it zeroes
     animation-name, which we never set inline). Idempotent: bail when the
     computed duration is unchanged, so re-inits (variant-change swap,
     resize, late live-span hydration) never restart the animation. */
  var PPF_SPEED_PX_PER_SEC = 25;
  function initPpfTrustMarquee(root) {
    var track = root.querySelector('.ils-ppf-track');
    if (!track) return;

    function evaluate() {
      var w = track.getBoundingClientRect().width;
      if (!(w > 0)) return;
      // One cycle = translateX(-50%) = half the (two-identical-set) track.
      var duration = (w / 2) / PPF_SPEED_PX_PER_SEC;
      var next = duration.toFixed(3) + 's';
      if (track.__ilsPpfDur === next) return; // unchanged → don't restart
      track.__ilsPpfDur = next;
      track.style.animationDuration = next;
    }

    evaluate();
    // The order-count and store-rating chips hydrate live after first paint
    // (custom-orders-count-live.js / rating), which changes chip widths — so
    // re-measure a couple of times, then keep it correct across resizes.
    setTimeout(evaluate, 400);
    setTimeout(evaluate, 1500);
    if (!track.__ilsPpfBound) {
      track.__ilsPpfBound = true;
      var rafToken = null;
      window.addEventListener('resize', function () {
        if (rafToken) cancelAnimationFrame(rafToken);
        rafToken = requestAnimationFrame(evaluate);
      });
    }
  }

  /* ---------- Bottom spacer under sticky panel (TASK-1873) ----------
     The footer is rendered as a separate section-group OUTSIDE .ils-pp,
     so .ils-pp{padding-bottom:…} doesn't extend the page's overall
     scroll runway — without help, the sticky panel covers the last
     line of the footer. Measure the live sticky height (two rows + the
     iOS safe-area inset) and expose it as --ils-pp-sticky-h on <body>,
     plus a marker class so the CSS-side padding only activates on the
     PDP. Re-measure on resize/orientationchange so rotation and
     dynamic-viewport changes stay accurate. */
  function syncStickyHeightVar(root) {
    var sticky = root.querySelector('.ils-pp__sticky-atc');
    if (!sticky) return;
    var h = Math.round(sticky.getBoundingClientRect().height);
    if (h <= 0) return;
    document.body.classList.add('ils-pp-has-sticky');
    document.body.style.setProperty('--ils-pp-sticky-h', h + 'px');
  }

  function initStickySpacer(root) {
    syncStickyHeightVar(root);
    if (root.__ilsSpacerBound) return;
    root.__ilsSpacerBound = true;
    var rafToken = null;
    function schedule() {
      if (rafToken) cancelAnimationFrame(rafToken);
      rafToken = requestAnimationFrame(function () { syncStickyHeightVar(root); });
    }
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
  }

  /* ---------- Weight legend force-correct (TASK-1886) ----------
     Belt-and-suspenders for the "Volume" vs "Choose weight" heading.
     The legend is server-rendered from the theme locale key
     `products.product.choose_weight`, but historically the same fieldset
     has been written to by custom-weight.js (legacy Dawn-picker rewrite
     path). custom-weight.js was fixed to skip .ils-pp__opt-label, but
     this safeguard guarantees that any rogue rewrite (third-party
     translation app, future Dawn DOM mutation, etc.) is undone on every
     init / variant-change. Reads the canonical value from a
     data-ils-weight-label attribute set in custom-main-product.liquid. */
  function enforceWeightLabel(root) {
    var scope = root || document;
    scope.querySelectorAll('.ils-pp__opt-label[data-ils-weight-label]').forEach(function (el) {
      var want = el.getAttribute('data-ils-weight-label') || '';
      if (!want) return;
      if ((el.textContent || '').trim() !== want) el.textContent = want;
    });
  }

  /* ---------- Init ---------- */
  function initRoot(root) {
    renderStars(root);
    initGallery(root);
    initTabs(root);
    initStickyAtc(root);
    initPayMarquee(root);
    initPpfTrustMarquee(root);
    initStickySpacer(root);
    enforceWeightLabel(root);
  }

  function initAll() {
    document.querySelectorAll('product-info.ils-pp-wrap').forEach(initRoot);
  }

  /*
   * Preview-theme shim. The dev/staging theme is served on the live primary
   * domain (lokumdokya.eu) via `?preview_theme_id=<id>` in the URL. On
   * variant change, Dawn's product-info.js builds a relative AJAX URL like
   * `/products/X?section_id=Y&option_values=Z` — without `preview_theme_id`
   * that request is answered by the PUBLISHED theme (Impulse), which has no
   * such `section_id`, so Shopify returns 404 and the price block never
   * updates. Fix: when `preview_theme_id` is present in the current page
   * URL, forward it (and `_fd=0` to bypass the Shop-app frame redirect) on
   * every section AJAX request, regardless of host. On production
   * (`preview_theme_id` absent) the patch is a no-op, so live behaviour is
   * untouched. TASK-1885.
   */
  function patchProductInfoPreviewUrls() {
    var pi = window.customElements && window.customElements.get && window.customElements.get('product-info');
    if (!pi || !pi.prototype || pi.prototype.__ilsPatched) return;
    var qs = new URLSearchParams(window.location.search);
    var urlThemeId = qs.get('preview_theme_id');
    var themeId = urlThemeId;
    if (!themeId) {
      // Legacy myshopify fallback (kept for direct shop-domain previews).
      if (!/myshopify\.com$/i.test(window.location.hostname)) return;
      var any = document.querySelector('product-info[data-theme-id]');
      themeId = any && any.dataset.themeId;
      if (!themeId) return;
    }
    var origBuild = pi.prototype.buildRequestUrlWithParams;
    pi.prototype.buildRequestUrlWithParams = function (url, optionValues, shouldFetchFullPage) {
      var built = origBuild.call(this, url, optionValues, shouldFetchFullPage);
      var sep = built.indexOf('?') >= 0 ? '&' : '?';
      built += sep + 'preview_theme_id=' + encodeURIComponent(themeId) + '&_fd=0';
      return built;
    };
    pi.prototype.__ilsPatched = true;
  }

  ready(patchProductInfoPreviewUrls);
  ready(setupVariantPriceSync);
  ready(initAll);

  // Re-init after variant-change AJAX swap (product-info re-renders subtree).
  document.addEventListener('product-info:loaded', function (e) {
    var root = e.target;
    if (root && root.classList && root.classList.contains('ils-pp-wrap')) {
      initRoot(root);
    }
  });
})();
